import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  Card,
  Typography,
  Input,
  Select,
  Row,
  Col,
  Tag,
  Alert,
  Button,
  Space,
  Modal,
  Descriptions,
  Tooltip,
  Image,
  DatePicker,
  Dropdown,
  message,
} from "antd";
import { useSelector } from "react-redux";
import { getDisputes } from "../../api/parkpass/parkpassDisputes";
import { resolveDispute } from "../../api/parkpass/parkpassDisputes";
import ParkPassAPI from "../../api/parkpassAxiosConfig";
import { useNavigate } from "react-router-dom";

import {
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  WarningOutlined,
  DownloadOutlined,
  EyeFilled,
  FilePdfOutlined,
  FileImageOutlined,
  FileExcelOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const statusColors = {
  pending: "orange",
  approved: "green",
  rejected: "red",
};

const statusLabels = {
  pending: "En attente",
  approved: "Approuvé",
  rejected: "Rejeté",
};

const CitizenDisputesPage = () => {
  const navigate = useNavigate();
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pdfPreviewVisible, setPdfPreviewVisible] = useState(false);
  const [attachmentPreviews, setAttachmentPreviews] = useState({});
  const [imagePreviewVisible, setImagePreviewVisible] = useState({});
  const [resolveLoading, setResolveLoading] = useState(false);
  const [adminNotes, setAdminNotes] = useState("");
  const [adminReply, setAdminReply] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    status: null,
    user_id: null,
    search: "",
  });
  const [dateRange, setDateRange] = useState([]);
  const [exporting, setExporting] = useState(false);

  const workerPdfRef = useRef(null);
  const workerExcelRef = useRef(null);

  const user = useSelector((state) => state.user.user);

  // Day-granular bounds: the backend widens `date_to` to the end of that day.
  const buildDateParams = (range = dateRange) => ({
    date_from: range?.[0] ? range[0].format("YYYY-MM-DD") : undefined,
    date_to: range?.[1] ? range[1].format("YYYY-MM-DD") : undefined,
  });

  const fetchDisputes = async (
    page = pagination.current,
    pageSize = pagination.pageSize,
    currentFilters = filters,
    currentDateRange = dateRange,
  ) => {
    setLoading(true);
    setError(null);
    try {
      const response = await getDisputes({
        page,
        pageSize,
        ...currentFilters,
        ...buildDateParams(currentDateRange),
      });
      // API returns { items: [], total: number, page: number, page_size: number }
      setDisputes(response.data.items || response.data);
      setPagination({
        current: response.data.page || 1,
        pageSize: response.data.page_size || pageSize,
        total: response.data.total ?? response.data.length,
      });
    } catch (err) {
      setError("Failed to fetch disputes. Please try again later.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDisputes();
    // Fetch once on mount only — every other fetch is triggered explicitly by
    // handlers below. Depending on `fetchDisputes` here would re-run this
    // effect on every pagination/filter change (it's a new function each
    // render), double-firing the request that handler already issued.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    workerPdfRef.current = new Worker(
      new URL("../../workers/pdfWorker.js", import.meta.url),
    );
    workerExcelRef.current = new Worker(
      new URL("../../workers/excelWorker.js", import.meta.url),
    );

    workerPdfRef.current.onmessage = (e) => {
      const { status, blobURL, operation, filename, message: msg } = e.data;
      switch (status) {
        case "complete":
          if (operation === "download") {
            const link = document.createElement("a");
            link.href = blobURL;
            link.download = filename || "file.pdf";
            link.click();
            URL.revokeObjectURL(blobURL);
          } else if (operation === "open") {
            window.open(blobURL, "_blank");
          }
          setExporting(false);
          break;
        case "error":
          message.error(`Erreur lors de la génération du PDF: ${msg}`);
          setExporting(false);
          break;
        default:
          break;
      }
    };

    workerExcelRef.current.onmessage = (e) => {
      const { success, blobURL, filename, error: workerError } = e.data;
      if (success) {
        const link = document.createElement("a");
        link.href = blobURL;
        link.download = filename || "file.xlsx";
        link.click();
        URL.revokeObjectURL(blobURL);
      } else {
        message.error(
          `Erreur lors de la génération du fichier Excel: ${workerError}`,
        );
      }
      setExporting(false);
    };

    return () => {
      workerPdfRef.current?.terminate();
      workerExcelRef.current?.terminate();
    };
  }, []);

  const handleTableChange = (newPagination) => {
    fetchDisputes(newPagination.current, newPagination.pageSize, filters);
  };

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = () => {
    fetchDisputes(1, pagination.pageSize, filters);
  };

  const handleReset = () => {
    const resetFilters = {
      status: null,
      user_id: null,
      search: "",
    };
    setFilters(resetFilters);
    setDateRange([]);
    // Pass the cleared range explicitly: setDateRange([]) won't be visible via
    // the `dateRange` closure until the next render.
    fetchDisputes(1, pagination.pageSize, resetFilters, []);
  };

  // --- Export (Web Worker-based, same skeleton as CitizenTransactionsPage) ---

  const formatCellDate = (date) =>
    date ? new Date(date).toLocaleString("fr-FR") : "";

  /** Fetch every dispute matching the current filters, replies included. */
  const fetchExportData = async () => {
    try {
      const response = await getDisputes({
        ...filters,
        ...buildDateParams(),
        no_pagination: true,
      });
      return response.data.items || response.data || [];
    } catch (err) {
      console.error("Error fetching export data:", err);
      message.error("Erreur lors de la récupération des données d'export");
      return null;
    }
  };

  const buildExportRows = (records) =>
    records.map((d) => ({
      id: d.id,
      reference: d.reference || "",
      infraction_id: d.infraction_id ?? "",
      matricule: d.matricule || "",
      amount: d.amount ?? 0,
      status: statusLabels[d.status] || d.status || "",
      reason: d.reason || "",
      username: d.username || `User ${d.user_id}`,
      admin_reply: d.admin_reply || "-",
      admin_notes: d.admin_notes || "-",
      created_at: formatCellDate(d.created_at),
      decided_at: formatCellDate(d.decided_at) || "-",
    }));

  const exportHeaders = [
    { text: "ID", type: "string", key: "id" },
    { text: "Référence", type: "string", key: "reference" },
    { text: "ID Infraction", type: "string", key: "infraction_id" },
    { text: "Plaque", type: "string", key: "matricule" },
    { text: "Montant (MAD)", type: "int", key: "amount", align: "right" },
    { text: "Statut", type: "string", key: "status" },
    { text: "Raison", type: "string", key: "reason" },
    { text: "Utilisateur", type: "string", key: "username" },
    { text: "Réponse apportée", type: "string", key: "admin_reply" },
    { text: "Notes internes", type: "string", key: "admin_notes" },
    { text: "Créé le", type: "string", key: "created_at" },
    { text: "Décidé le", type: "string", key: "decided_at" },
  ];

  const buildExportPayload = (records) => {
    const rows = buildExportRows(records);
    return [
      [
        "Litiges sur les infractions",
        "",
        `Date à partir de : ${dateRange?.[0]?.format("YYYY-MM-DD") || "N/A"}`,
        `Date jusqu'au : ${dateRange?.[1]?.format("YYYY-MM-DD") || "N/A"}`,
      ],
      [],
      [],
      user?.full_name || "Rapport d'exportation",
      [],
      [exportHeaders],
      [rows],
      window.location.origin,
      {
        total: rows.reduce((sum, r) => sum + parseFloat(r.amount || 0), 0),
        label: "Total",
      },
    ];
  };

  const handleExcelClick = async () => {
    setExporting(true);
    const records = await fetchExportData();
    if (!records) {
      setExporting(false);
      return;
    }
    const timestamp = new Date().toISOString().replace(/:/g, "-");
    workerExcelRef.current.postMessage({
      data: buildExportPayload(records),
      filename: `litiges_infractions_${timestamp}.xlsx`,
    });
  };

  const handlePdfClick = async (operation) => {
    setExporting(true);
    const records = await fetchExportData();
    if (!records) {
      setExporting(false);
      return;
    }
    const timestamp = new Date().toISOString().replace(/:/g, "-");
    workerPdfRef.current.postMessage({
      data: buildExportPayload(records),
      operation,
      filename: `litiges_infractions_${timestamp}.pdf`,
    });
  };

  const pdfMenuItems = [
    {
      key: "download",
      label: "Télécharger",
      onClick: () => handlePdfClick("download"),
      disabled: exporting,
    },
    {
      key: "open",
      label: "Ouvrir",
      onClick: () => handlePdfClick("open"),
      disabled: exporting,
    },
  ];

  // --- End Export ---

  const handleViewDetail = (record) => {
    setSelectedDispute(record);
    setDetailModalVisible(true);
  };

  const handleCloseDetail = () => {
    setDetailModalVisible(false);
    setSelectedDispute(null);
    setAdminNotes("");
    setAdminReply("");
  };

  const handleResolve = (status) => {
    Modal.confirm({
      title: `Confirmer ${status === "approved" ? "l'approbation" : "le rejet"} du litige`,
      content: `Êtes-vous sûr de vouloir ${status === "approved" ? "approuver" : "rejeter"} ce litige ?`,
      okText: status === "approved" ? "Approuver" : "Rejeter",
      okType: status === "approved" ? "primary" : "danger",
      cancelText: "Annuler",
      onOk: async () => {
        try {
          setResolveLoading(true);
          await resolveDispute(
            selectedDispute.id,
            status,
            adminNotes || undefined,
            adminReply.trim() || undefined,
          );
          setDetailModalVisible(false);
          setSelectedDispute(null);
          setAdminNotes("");
          setAdminReply("");
          fetchDisputes(pagination.current, pagination.pageSize, filters);
        } catch (err) {
          console.error("Error resolving dispute:", err);
        } finally {
          setResolveLoading(false);
        }
      },
    });
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Infraction",
      key: "infraction",
      render: (_, record) => (
        <div>
          <div>
            Ref: <strong>{record.reference}</strong>
          </div>
          <div style={{ fontSize: 12, color: "#999" }}>
            ID: {record.infraction_id}
          </div>
        </div>
      ),
    },
    {
      title: "Utilisateur",
      key: "user",
      render: (_, record) => (
        <div>
          {record.username && <div>{record.username}</div>}
          <div style={{ fontSize: 12, color: "#999" }}>
            ID: {record.user_id}
          </div>
        </div>
      ),
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={statusColors[status] || "default"}>
          {statusLabels[status] || status}
        </Tag>
      ),
    },
    {
      title: "Raison",
      dataIndex: "reason",
      key: "reason",
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <span>{text}</span>
        </Tooltip>
      ),
    },
    {
      title: "Montant",
      dataIndex: "amount",
      key: "amount",
      render: (amount) => `${amount} MAD`,
    },
    {
      title: "Plaque",
      dataIndex: "matricule",
      key: "matricule",
    },
    {
      title: "Date de création",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => new Date(text).toLocaleString("fr-FR"),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      render: (_, record) => (
        <Button
          type="primary"
          icon={<EyeOutlined />}
          size="small"
          onClick={() => handleViewDetail(record)}
        >
          Détails
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space>
            <WarningOutlined />
            <Title level={4} style={{ margin: 0 }}>
              Litiges sur les infractions
            </Title>
            <Tooltip
              title="Un litige soumis par le client reste « En attente » tant qu'un agent ne l'a pas traité. Les messages (notes internes et réponse au client) ne sont enregistrés qu'au moment de l'approbation ou du rejet. Une fois approuvé ou rejeté, la décision et les messages sont définitifs et ne peuvent plus être modifiés."
            >
              <InfoCircleOutlined style={{ color: "#1890ff", cursor: "help" }} />
            </Tooltip>
          </Space>
        }
      >
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Rechercher (Réf, Plaque, Utilisateur, Email...)"
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              onPressEnter={handleSearch}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Select
              placeholder="Filtrer par Statut"
              style={{ width: "100%" }}
              onChange={(value) => handleFilterChange("status", value)}
              value={filters.status}
              allowClear
            >
              <Option value="pending">En attente</Option>
              <Option value="approved">Approuvé</Option>
              <Option value="rejected">Rejeté</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Input
              placeholder="Filtrer par User ID"
              value={filters.user_id}
              onChange={(e) => handleFilterChange("user_id", e.target.value)}
              onPressEnter={handleSearch}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <RangePicker
              placeholder={["Date de début", "Date de fin"]}
              style={{ width: "100%" }}
              value={dateRange}
              onChange={(dates) => setDateRange(dates || [])}
              format="YYYY-MM-DD"
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Space>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
              >
                Rechercher
              </Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                Réinitialiser
              </Button>
            </Space>
          </Col>
        </Row>
        <div style={{ marginBottom: 16, gap: 8 }} className="flex justify-end">
          <Button
            icon={<FileExcelOutlined />}
            onClick={handleExcelClick}
            disabled={disputes.length === 0 || exporting}
          >
            Export Excel
          </Button>
          <Dropdown
            menu={{ items: pdfMenuItems }}
            disabled={disputes.length === 0 || exporting}
          >
            <Button
              icon={<FilePdfOutlined />}
              disabled={disputes.length === 0 || exporting}
            >
              Export PDF{" "}
              {exporting ? "..." : <span style={{ marginLeft: 5 }}>▼</span>}
            </Button>
          </Dropdown>
        </div>
        {error && <Alert message={error} type="error" showIcon />}
        <Table
          dataSource={disputes}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} litiges`,
            pageSizeOptions: ["10", "25", "50", "100"],
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
        />
        <Modal
          title="Détails du litige"
          open={detailModalVisible}
          onCancel={handleCloseDetail}
          footer={[
            selectedDispute?.status === "pending" && (
              <Button
                key="reject"
                danger
                icon={<CloseCircleOutlined />}
                onClick={() => handleResolve("rejected")}
                loading={resolveLoading}
              >
                Rejeter
              </Button>
            ),
            selectedDispute?.status === "pending" && (
              <Button
                key="approve"
                type="primary"
                style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
                icon={<CheckCircleOutlined />}
                onClick={() => handleResolve("approved")}
                loading={resolveLoading}
              >
                Approuver
              </Button>
            ),
            <Button key="close" onClick={handleCloseDetail}>
              Fermer
            </Button>,
          ].filter(Boolean)}
          width={700}
        >
          {selectedDispute && (
            <div>
              <div style={{ marginBottom: 12, textAlign: "right" }}>
                <Button
                  icon={<EyeOutlined />}
                  onClick={() =>
                    window.open(
                      `/infraction/${selectedDispute?.infraction_id}`,
                      "_blank",
                    )
                  }
                >
                  Voir l'infraction
                </Button>
              </div>
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="ID Litige">
                  {selectedDispute.id}
                </Descriptions.Item>
                <Descriptions.Item label="ID Infraction">
                  {selectedDispute.infraction_id}
                </Descriptions.Item>
                <Descriptions.Item label="Référence Infraction">
                  {selectedDispute.reference}
                </Descriptions.Item>
                <Descriptions.Item label="Plaque d'immatriculation">
                  {selectedDispute.matricule}
                </Descriptions.Item>
                <Descriptions.Item label="Montant">
                  {selectedDispute.amount} MAD
                </Descriptions.Item>
                <Descriptions.Item label="Statut du litige">
                  <Tag
                    color={statusColors[selectedDispute.status] || "default"}
                  >
                    {statusLabels[selectedDispute.status] ||
                      selectedDispute.status}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Statut de l'infraction">
                  {selectedDispute.infraction_status}
                </Descriptions.Item>
                <Descriptions.Item label="Raison du litige">
                  {selectedDispute.reason}
                </Descriptions.Item>
                <Descriptions.Item label="ID Utilisateur">
                  {selectedDispute.user_id}
                </Descriptions.Item>
                <Descriptions.Item label="Date de création">
                  {new Date(selectedDispute.created_at).toLocaleString("fr-FR")}
                </Descriptions.Item>
                {selectedDispute.decided_at && (
                  <Descriptions.Item label="Date de décision">
                    {new Date(selectedDispute.decided_at).toLocaleString(
                      "fr-FR",
                    )}
                  </Descriptions.Item>
                )}
                {selectedDispute.admin_notes && (
                  <Descriptions.Item label="Notes admin (interne)">
                    {selectedDispute.admin_notes}
                  </Descriptions.Item>
                )}
                {selectedDispute.admin_reply && (
                  <Descriptions.Item label="Réponse au client">
                    <div style={{ whiteSpace: "pre-wrap" }}>
                      {selectedDispute.admin_reply}
                    </div>
                  </Descriptions.Item>
                )}
              </Descriptions>

              {selectedDispute.status === "pending" && (
                <div style={{ marginTop: 16 }}>
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="Les notes internes et la réponse au client ne seront enregistrées qu'au moment de l'approbation ou du rejet. Après décision, elles ne pourront plus être modifiées."
                  />
                  <Title level={5}>Notes de l'administrateur (interne)</Title>
                  <Input.TextArea
                    rows={3}
                    placeholder="Ajouter des notes internes pour la décision (optionnel, non visible par le client)"
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                  />
                  <Title level={5} style={{ marginTop: 16 }}>
                    Réponse au client (visible par le client)
                  </Title>
                  <Input.TextArea
                    rows={3}
                    placeholder="Expliquez votre décision au client… (optionnel)"
                    value={adminReply}
                    onChange={(e) => setAdminReply(e.target.value)}
                    maxLength={2000}
                    showCount
                  />
                </div>
              )}

              <div style={{ marginTop: 16 }}>
                <Title level={5}>Pièces jointes</Title>
                {(!selectedDispute.attachments ||
                  selectedDispute.attachments.length === 0) && (
                  <Alert message="Aucune pièce jointe" type="info" showIcon />
                )}
                {(selectedDispute.attachments || []).map((att) => {
                  const isImage = att.content_type?.startsWith("image/");
                  const isPdf = att.content_type === "application/pdf";
                  const previewUrl = attachmentPreviews[att.id];

                  const formatBytes = (bytes) => {
                    if (bytes === undefined || bytes === null) return "-";
                    if (bytes === 0) return "0 B";
                    const k = 1024;
                    const sizes = ["B", "KB", "MB", "GB", "TB"];
                    const i = Math.floor(Math.log(bytes) / Math.log(k));
                    const value = bytes / Math.pow(k, i);
                    const decimals = i === 0 ? 0 : value < 10 ? 2 : 1;
                    return `${parseFloat(value.toFixed(decimals))} ${sizes[i]}`;
                  };

                  const sizeLabel = formatBytes(att.size_bytes);

                  const handlePreview = async () => {
                    try {
                      const resp = await ParkPassAPI.get(
                        `/v1/infractions/disputes/${selectedDispute.id}/attachments/${att.id}`,
                        { responseType: "blob" },
                      );
                      const url = window.URL.createObjectURL(resp.data);

                      if (isPdf) {
                        setPdfPreviewUrl(url);
                        setPdfPreviewVisible(true);
                      } else if (isImage) {
                        setAttachmentPreviews((prev) => ({
                          ...prev,
                          [att.id]: url,
                        }));
                        setImagePreviewVisible((prev) => ({
                          ...prev,
                          [att.id]: true,
                        }));
                      }
                    } catch (err) {
                      console.error("Preview failed", err);
                    }
                  };

                  const handleDownload = async () => {
                    try {
                      const resp = await ParkPassAPI.get(
                        `/v1/infractions/disputes/${selectedDispute.id}/attachments/${att.id}`,
                        { responseType: "blob" },
                      );
                      const url = window.URL.createObjectURL(resp.data);
                      const link = document.createElement("a");
                      link.href = url;
                      link.setAttribute(
                        "download",
                        att.filename || "attachment",
                      );
                      document.body.appendChild(link);
                      link.click();
                      link.parentNode.removeChild(link);
                      window.URL.revokeObjectURL(url);
                    } catch (err) {
                      console.error("Download failed", err);
                    }
                  };

                  return (
                    <Card size="small" style={{ marginTop: 8 }} key={att.id}>
                      <Row justify="space-between" align="middle">
                        <Col>
                          <Space>
                            {isImage && (
                              <FileImageOutlined
                                style={{ fontSize: 20, color: "#1890ff" }}
                              />
                            )}
                            {isPdf && (
                              <FilePdfOutlined
                                style={{ fontSize: 20, color: "#ff4d4f" }}
                              />
                            )}
                            <div>
                              <div style={{ fontWeight: 600 }}>
                                {att.filename}
                              </div>
                              <div style={{ fontSize: 12, color: "#666" }}>
                                {att.content_type} • {sizeLabel}
                              </div>
                            </div>
                          </Space>
                        </Col>
                        <Col>
                          <Space>
                            {isImage && previewUrl && (
                              <Image
                                width={0}
                                height={0}
                                style={{ display: "none" }}
                                src={previewUrl}
                                preview={{
                                  visible: imagePreviewVisible[att.id] || false,
                                  onVisibleChange: (visible) => {
                                    setImagePreviewVisible((prev) => ({
                                      ...prev,
                                      [att.id]: visible,
                                    }));
                                    if (!visible && previewUrl) {
                                      window.URL.revokeObjectURL(previewUrl);
                                      setAttachmentPreviews((prev) => {
                                        const updated = { ...prev };
                                        delete updated[att.id];
                                        return updated;
                                      });
                                    }
                                  },
                                }}
                              />
                            )}
                            {(isImage || isPdf) && (
                              <Button
                                type="primary"
                                icon={<EyeFilled />}
                                onClick={handlePreview}
                              ></Button>
                            )}
                            <Button
                              type="link"
                              icon={<DownloadOutlined />}
                              onClick={handleDownload}
                            ></Button>
                          </Space>
                        </Col>
                      </Row>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}
        </Modal>

        <Modal
          title="Aperçu PDF"
          open={pdfPreviewVisible}
          onCancel={() => {
            setPdfPreviewVisible(false);
            if (pdfPreviewUrl) {
              window.URL.revokeObjectURL(pdfPreviewUrl);
              setPdfPreviewUrl(null);
            }
          }}
          footer={null}
          width="90%"
          style={{ top: 20 }}
        >
          {pdfPreviewUrl && (
            <iframe
              src={pdfPreviewUrl}
              style={{ width: "100%", height: "80vh", border: "none" }}
              title="PDF Preview"
            />
          )}
        </Modal>
      </Card>
    </div>
  );
};

export default CitizenDisputesPage;
